import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui';
import { useAuth } from '../../../app/auth';
import {
  Users,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  Banknote,
  RefreshCw,
  X
} from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { diaOperativo, formatTimeCR } from '../../../utils/date';

interface BackendTicket {
  id: string;
  vendedor_id: string | null;
  vendedor_nombre?: string | null;
  local_id: string;
  sorteo_id: string;
  sorteo_nombre?: string | null;
  total: string;
  metodo_pago: string | null;
  referencia: string | null;
  created_at: string;
  ticket_number: string | null;
  estado: string;
  jugadas: Array<{ estado: string }>;
}

interface BackendSorteo {
  id: string;
  nombre: string;
}

const METODOS = [
  { key: 'EFECTIVO', label: 'Efectivo', color: 'from-green-500 to-green-600', light: 'text-green-100', icon: Banknote, badge: 'bg-green-100 text-green-800' },
];

export const LocalDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<BackendTicket[]>([]);
  const [sorteosMap, setSorteosMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [filtroMetodo, setFiltroMetodo] = useState<string | null>(null);

  const fetchData = () => {
    setLoading(true);
    const hoy = diaOperativo();
    Promise.all([
      fetchAPI<BackendTicket[]>(`/tickets?fecha=${hoy}`),
      fetchAPI<BackendSorteo[]>('/sorteos'),
    ])
      .then(([ticketsData, sorteosData]) => {
        setTickets(ticketsData);
        setSorteosMap(
          (sorteosData || []).reduce<Record<string, string>>((acc, s) => {
            acc[s.id] = s.nombre;
            return acc;
          }, {})
        );
      })
      .catch(err => console.error('Error cargando datos:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // Calculos — excluir tickets anulados
  const ticketsActivos = tickets.filter(t => t.estado !== 'ANULADO');
  const ventasHoy = ticketsActivos.reduce((sum, t) => sum + parseFloat(t.total), 0);
  const ticketsHoy = ticketsActivos.length;

  const porMetodo = (metodo: string) => {
    const filtered = ticketsActivos.filter(t => t.metodo_pago === metodo);
    return { count: filtered.length, total: filtered.reduce((s, t) => s + parseFloat(t.total), 0) };
  };

  // Nombre vendedor proveniente del backend (scope local aplicado en API)
  const nombreVendedor = (ticket: BackendTicket) => {
    if (ticket.vendedor_nombre) return ticket.vendedor_nombre;
    if (ticket.vendedor_id) return ticket.vendedor_id;
    return 'Vendedor';
  };

  // Tickets filtrados para la tabla
  const ticketsFiltrados = filtroMetodo
    ? tickets.filter(t => t.metodo_pago === filtroMetodo)
    : tickets;

  const metodoActivo = filtroMetodo
    ? METODOS.find(m => m.key === filtroMetodo)
    : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500">
            {new Date().toLocaleDateString('es-CR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title="Refrescar"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Banner principal - Total del dia */}
      <div
        className={`cursor-pointer rounded-xl p-4 text-white bg-gradient-to-br from-blue-600 to-blue-700 transition-all ${filtroMetodo === null ? 'ring-2 ring-blue-300 ring-offset-2' : 'opacity-80 hover:opacity-100'}`}
        onClick={() => setFiltroMetodo(null)}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="text-blue-100 text-sm">Ventas Hoy</p>
            <p className="text-2xl font-bold">{'\u20A1'}{ventasHoy.toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">Tickets</p>
            <p className="text-2xl font-bold">{ticketsHoy}</p>
          </div>
        </div>
      </div>

      {/* Banner por metodo de pago */}
      <div className="grid grid-cols-1 gap-3">
        {METODOS.map(m => {
          const data = porMetodo(m.key);
          const Icon = m.icon;
          const activo = filtroMetodo === m.key;
          return (
            <div
              key={m.key}
              className={`cursor-pointer rounded-xl p-3 text-white bg-gradient-to-br ${m.color} transition-all ${activo ? 'ring-2 ring-offset-2 ring-gray-400 scale-[1.02]' : 'opacity-75 hover:opacity-100'}`}
              onClick={() => setFiltroMetodo(activo ? null : m.key)}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5" />
                <span className={`text-xs ${m.light}`}>{m.label}</span>
              </div>
              <p className="text-lg font-bold mt-1">{'\u20A1'}{data.total.toLocaleString()}</p>
              <p className={`text-xs ${m.light}`}>{data.count} ticket{data.count !== 1 ? 's' : ''}</p>
            </div>
          );
        })}
      </div>

      {/* Tabla de detalle */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">
            {filtroMetodo
              ? `Detalle: ${metodoActivo?.label}`
              : 'Todos los tickets'}
            <span className="text-sm font-normal text-gray-500 ml-2">({ticketsFiltrados.length})</span>
          </h3>
          {filtroMetodo && (
            <button
              onClick={() => setFiltroMetodo(null)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 px-2 py-1 rounded hover:bg-red-50"
            >
              <X size={14} /> Quitar filtro
            </button>
          )}
        </div>

        {ticketsFiltrados.length === 0 ? (
          <p className="text-center text-gray-400 py-6 text-sm">No hay tickets{filtroMetodo ? ` de ${metodoActivo?.label}` : ''}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-3 py-2 font-semibold"># Ticket</th>
                  <th className="text-left px-3 py-2 font-semibold">Hora</th>
                  <th className="text-left px-3 py-2 font-semibold">Vendedor</th>
                  <th className="text-left px-3 py-2 font-semibold">Sorteo</th>
                  <th className="text-center px-3 py-2 font-semibold">Jugadas</th>
                  <th className="text-center px-3 py-2 font-semibold">Pago</th>
                  <th className="text-left px-3 py-2 font-semibold">Referencia</th>
                  <th className="text-right px-3 py-2 font-semibold">Total</th>
                  <th className="text-center px-3 py-2 font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {ticketsFiltrados.map(t => {
                  const metodo = METODOS.find(m => m.key === t.metodo_pago);
                  const hora = formatTimeCR(t.created_at);
                  const sorteoLabel = t.sorteo_nombre || sorteosMap[t.sorteo_id] || '—';
                  return (
                    <tr key={t.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono font-medium">{t.ticket_number || t.id.slice(0, 10)}</td>
                      <td className="px-3 py-2 text-gray-600">{hora}</td>
                      <td className="px-3 py-2">{nombreVendedor(t)}</td>
                      <td className="px-3 py-2 text-gray-600">{sorteoLabel}</td>
                      <td className="px-3 py-2 text-center">{t.jugadas.length}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${metodo?.badge || 'bg-gray-100 text-gray-700'}`}>
                          {t.metodo_pago || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs">{t.referencia || '-'}</td>
                      <td className="px-3 py-2 text-right font-medium">{'\u20A1'}{parseFloat(t.total).toLocaleString()}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${t.estado === 'ABIERTO' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {t.estado === 'ABIERTO' ? 'VENDIDO' : t.estado}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-3 py-2" colSpan={7}>Total</td>
                  <td className="px-3 py-2 text-right">
                    {'\u20A1'}{ticketsFiltrados.filter(t => t.estado !== 'ANULADO').reduce((s, t) => s + parseFloat(t.total), 0).toLocaleString()}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Quick actions */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'POS', icon: Users, path: '/local/pos' },
            { label: 'Listas', icon: Users, path: '/local/listas' },
            { label: 'Caja Lotería', icon: DollarSign, path: '/local/caja-loteria' },
            { label: 'Reportes', icon: TrendingUp, path: '/local/reportes' },
          ].map((action, i) => (
            <a
              key={i}
              href={action.path}
              className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-xl hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              <action.icon className="w-6 h-6" />
              <span className="text-sm font-medium">{action.label}</span>
            </a>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default LocalDashboardPage;
